import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost } from "../../lib";

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1000, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  blurb: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  actions: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  from: { color: theme.palette.text.disabled },
  to: { fontWeight: 600 },
  error: { color: theme.palette.error.main },
  notice: { color: theme.palette.success.main },
}));

const money = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export function PreownedRefreshMsrpPage() {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const preview = useCallback(async () => {
    setLoading(true);
    setError("");
    const { data, error: err } = await authGet("/preowned/refresh-msrp");
    setLoading(false);
    if (err) return setError(err.message || "Preview failed");
    setRows(data?.rows ?? []);
  }, []);

  useEffect(() => {
    preview();
  }, [preview]);

  async function run() {
    if (
      !window.confirm(
        `Update the MSRP on ${rows.length} item(s)? Each change is recorded in the activity log.`,
      )
    )
      return;
    setRunning(true);
    setError("");
    setNotice("");
    const { data, error: err } = await authPost("/preowned/refresh-msrp", {});
    setRunning(false);
    if (err) return setError(err.message || "Refresh failed");
    setNotice(`Updated ${data?.updated ?? 0} item(s).`);
    preview();
  }

  return (
    <Box p={3} className={classes.page}>
      <Typography variant="h5" gutterBottom>
        Refresh MSRP
      </Typography>
      <Typography variant="body2" className={classes.blurb}>
        Copies the retail price from the main catalog onto live pre-owned items
        (In Stock, Available Soon, On Hold) whose model number matches. Items
        already holding the right price aren't touched.
      </Typography>

      <Paper className={classes.panel}>
        <div className={classes.actions}>
          <Button variant="outlined" size="small" onClick={preview} disabled={loading}>
            {loading ? "Checking…" : "Re-check"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={run}
            disabled={running || loading || !rows.length}
          >
            {running ? "Updating…" : `Update ${rows.length} item${rows.length === 1 ? "" : "s"}`}
          </Button>
          {error && <Typography className={classes.error}>{error}</Typography>}
          {notice && <Typography className={classes.notice}>{notice}</Typography>}
          {!loading && !rows.length && !error && (
            <Typography variant="body2" color="textSecondary">
              Everything is already up to date.
            </Typography>
          )}
        </div>
      </Paper>

      {!!rows.length && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Stock ID</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">New</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.item_id} hover>
                  <TableCell>{r.stock_id}</TableCell>
                  <TableCell>{r.brand}</TableCell>
                  <TableCell>{r.model_no}</TableCell>
                  <TableCell align="right" className={classes.from}>
                    {money(r.current_msrp)}
                  </TableCell>
                  <TableCell align="right" className={classes.to}>
                    {money(r.new_msrp)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
export default PreownedRefreshMsrpPage;
